import React, { useEffect, useState } from 'react';
import { useParamsContext } from '../../contexts/ParamsContext';
import { AtendentesService } from './Service';
import { useAppContext } from '../../contexts/AppContext';
import { useAtendentesContext } from './Context';
import { getPlanErrorMessage } from '../../plans/planErrors';
import FileUploadUser from '../../components/files/FileUploadUser';
import AvatarUser from '../../components/AvatarUser';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { SettingsRow, SettingsSection } from '../configuracoes/estabelecimento/Form/SettingsGroup';
import { UserX } from 'lucide-react';

const service = new AtendentesService();

export default function AtendentesForm() {
    const { clear, id } = useParamsContext();
    const { fetchAtendentes, atendentes, loading: loadingList } = useAtendentesContext();
    const { successSnackbar, errorSnackbar } = useAppContext();
    const [loading, setLoading] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [admin, setAdmin] = useState(false);
    const [comissao, setComissao] = useState<number | ''>(0);
    const [photoURL, setPhotoURL] = useState('');
    const [uploadingImage, setUploadingImage] = useState(false);
    const isEditing = !!id;
    const attendant = atendentes.find((item) => item.uid === id);
    const attendantUid = attendant?.uid;

    // Preenche ao abrir: recarregar a lista nao pode apagar o que foi digitado.
    useEffect(() => {
        if (!attendant) return;

        setName(attendant.displayName || '');
        setEmail(attendant.email || '');
        setAdmin(attendant.type === 'admin');
        setComissao(Number(attendant.comissao) || 0);
        setPhotoURL(attendant.photoURL || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attendantUid]);

    const submit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (uploadingImage) {
            errorSnackbar('Aguarde o upload da imagem concluir para salvar.');
            return;
        }

        const comissaoValue = Number(comissao) || 0;

        try {
            setLoading(true);

            if (id) {
                await service.update(id, {
                    admin: admin ? 'sim' : 'nao',
                    comissao: comissaoValue,
                    photoURL: photoURL || null,
                });
                successSnackbar('Atendente atualizado com sucesso!');
            } else {
                await service.create({
                    name: name.trim(),
                    email: email.trim(),
                    admin,
                    comissao: comissaoValue,
                    photoURL: photoURL || undefined,
                });
                successSnackbar('Atendente criado com sucesso!');
            }

            await fetchAtendentes();
            clear();
        } catch (e: any) {
            // Mantem o formulario aberto: o erro comum e o limite do plano ou e-mail em uso.
            errorSnackbar(getPlanErrorMessage(e, 'Ocorreu um erro ao salvar o atendente.'));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            setDeleting(true);
            await service.delete(id);
            await fetchAtendentes();
            successSnackbar('Atendente removido.');
            clear();
        } catch (e) {
            errorSnackbar('Ocorreu um erro ao remover o atendente.');
            setDeleting(false);
            setOpenDelete(false);
        }
    };

    if (isEditing && loadingList) {
        return (
            <div className="space-y-3 px-2" aria-busy="true">
                <div className="sf-skeleton h-24" />
                <div className="sf-skeleton h-32" />
            </div>
        );
    }

    if (isEditing && !attendant) {
        return (
            <div className="sf-state">
                <UserX aria-hidden />
                <h2>Atendente não encontrado</h2>
                <button type="button" className="sf-link" onClick={clear}>
                    Voltar para atendentes
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="estab-settings sf-page mx-auto max-w-4xl px-2">
            <div className="grid gap-6 xl:grid-cols-2 xl:items-start">
                <div className="space-y-6">
                    <SettingsSection title="Foto">
                        <FileUploadUser value={photoURL} setValue={setPhotoURL} onLoadingChange={setUploadingImage}>
                            {({ loading: loadingFile, fileData, error, handleChange, removeFile, inputRef }) => {
                                const previewUrl = fileData?.url;
                                const isBusy = loading || loadingFile;

                                return (
                                    <div className="flex items-center gap-4 p-4">
                                        <div className="relative shrink-0">
                                            <AvatarUser
                                                name={name || 'Atendente'}
                                                photoURL={previewUrl}
                                                size="h-20 w-20 !text-2xl"
                                            />
                                            {loadingFile && (
                                                <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40">
                                                    <span className="loading loading-spinner loading-sm text-white"></span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex min-w-0 flex-col items-start gap-1">
                                            <input
                                                disabled={isBusy}
                                                type="file"
                                                ref={inputRef}
                                                accept="image/*"
                                                onChange={handleChange}
                                                className="hidden"
                                            />
                                            <button
                                                type="button"
                                                className="estab-link-button"
                                                disabled={isBusy}
                                                onClick={() => inputRef.current?.click()}
                                            >
                                                {previewUrl ? 'Alterar foto' : 'Escolher foto'}
                                            </button>
                                            {!!previewUrl && (
                                                <button
                                                    type="button"
                                                    className="estab-link-button estab-link-button--destructive"
                                                    disabled={isBusy}
                                                    onClick={removeFile}
                                                >
                                                    Remover
                                                </button>
                                            )}
                                            {!!error && <div className="text-xs text-error">{error}</div>}
                                        </div>
                                    </div>
                                );
                            }}
                        </FileUploadUser>
                    </SettingsSection>

                    <SettingsSection
                        title="Dados"
                        footer={isEditing ? 'Nome e e-mail pertencem à conta do atendente e não são alterados aqui.' : undefined}
                    >
                        {isEditing ? (
                            <>
                                <SettingsRow label="Nome">
                                    <span className="estab-row-value">{name || '—'}</span>
                                </SettingsRow>
                                <SettingsRow label="E-mail">
                                    <span className="estab-row-value">{email || '—'}</span>
                                </SettingsRow>
                            </>
                        ) : (
                            <>
                                <SettingsRow label="Nome" htmlFor="atendente-nome">
                                    <input
                                        id="atendente-nome"
                                        type="text"
                                        className="input estab-field"
                                        placeholder="Nome do atendente"
                                        value={name}
                                        required
                                        disabled={loading}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                </SettingsRow>
                                <SettingsRow label="E-mail" htmlFor="atendente-email">
                                    <input
                                        id="atendente-email"
                                        type="email"
                                        className="input estab-field"
                                        placeholder="email@exemplo.com"
                                        autoComplete="off"
                                        value={email}
                                        required
                                        disabled={loading}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </SettingsRow>
                            </>
                        )}
                    </SettingsSection>
                </div>

                <div className="space-y-6">
                    <SettingsSection title="Trabalho">
                        <SettingsRow label="Comissão (%)" htmlFor="atendente-comissao">
                            <input
                                id="atendente-comissao"
                                type="number"
                                className="input estab-field"
                                placeholder="0"
                                value={comissao}
                                min={0}
                                max={100}
                                disabled={loading}
                                onChange={(e) => setComissao(e.target.value === '' ? '' : Number(e.target.value))}
                            />
                        </SettingsRow>
                        <SettingsRow
                            label="Administrador"
                            htmlFor="atendente-admin"
                            description="Acesso às áreas de gestão do estabelecimento."
                            inline
                        >
                            <input
                                id="atendente-admin"
                                type="checkbox"
                                className="toggle toggle-success"
                                checked={admin}
                                disabled={loading}
                                onChange={(e) => setAdmin(e.target.checked)}
                            />
                        </SettingsRow>
                    </SettingsSection>

                    {isEditing && (
                        <div className="sf-group">
                            <button type="button" className="sf-destructive-row" onClick={() => setOpenDelete(true)}>
                                Remover atendente
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="estab-savebar mt-6 gap-2">
                <button
                    type="button"
                    className="btn btn-ghost flex-1 sm:flex-none sm:min-w-[8rem]"
                    onClick={clear}
                    disabled={loading}
                >
                    Cancelar
                </button>
                <button
                    className="btn btn-primary flex-1 sm:flex-none sm:min-w-[11rem]"
                    type="submit"
                    disabled={loading || uploadingImage}
                >
                    {loading && <span className="loading loading-spinner loading-sm"></span>}
                    {isEditing ? 'Salvar alterações' : 'Adicionar atendente'}
                </button>
            </div>

            {openDelete && (
                <ConfirmDialog
                    title="Remover atendente?"
                    confirmLabel="Remover"
                    loading={deleting}
                    onConfirm={handleDelete}
                    onClose={() => setOpenDelete(false)}
                >
                    {name || 'O atendente'} será removido deste estabelecimento e perderá o acesso a ele.
                </ConfirmDialog>
            )}
        </form>
    );
}
